import { useEffect, useCallback, useRef, useState, type ReactNode } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { uploadAdminMedia } from '../media'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { ResizableImage } from './ResizableImage'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import CharacterCount from '@tiptap/extension-character-count'
import Highlight from '@tiptap/extension-highlight'
import Youtube from '@tiptap/extension-youtube'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link2,
  ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo2,
  Redo2,
  Minus,
  Table as TableIcon,
  Youtube as YoutubeIcon,
  Highlighter,
  RemoveFormatting,
} from 'lucide-react'

type Props = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
  className?: string
}

function ToolBtn({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition ${
        active
          ? 'bg-primary text-white shadow-sm'
          : 'text-slate-600 hover:bg-primary/10 hover:text-primary'
      } disabled:opacity-40`}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-primary/15 shrink-0" aria-hidden />
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start writing… Use headings, lists, and short paragraphs for SEO & readability.',
  minHeight = 360,
  className = '',
}: Props) {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Highlight.configure({ multicolor: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', class: 'text-primary underline' },
      }),
      ResizableImage,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
      CharacterCount,
      Youtube.configure({
        modestBranding: true,
        HTMLAttributes: { class: 'rounded-xl overflow-hidden aspect-video w-full' },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: `prose prose-sm sm:prose max-w-none focus:outline-none px-4 py-3 sm:px-5 sm:py-4 min-h-[${minHeight}px] prose-headings:font-display prose-a:text-primary`,
        style: `min-height:${minHeight}px`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML())
    },
  })

  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    const next = value || ''
    if (next !== current && next !== '<p></p>') {
      // Avoid cursor jump when parent re-sets same logical empty
      if (stripEmpty(next) !== stripEmpty(current)) {
        editor.commands.setContent(next, { emitUpdate: false })
      }
    }
  }, [value, editor])

  const setLink = useCallback(() => {
    if (!editor) return
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL (use /path for internal links)', prev || 'https://')
    if (url === null) return
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }, [editor])

  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addImage = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const onPickImage = useCallback(
    async (file: File | null | undefined) => {
      if (!editor || !file) return
      setUploading(true)
      try {
        const up = await uploadAdminMedia(file)
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'image',
            attrs: {
              src: up.url,
              alt: file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
              'data-cdn-path': up.relativePath || up.storedPath.replace(/^cdn:/, ''),
              'data-width-percent': '100',
              'data-align': 'center',
            },
          })
          .run()
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Image upload failed')
      } finally {
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [editor, onChange]
  )

  const addYoutube = useCallback(() => {
    if (!editor) return
    const url = window.prompt('YouTube URL')
    if (!url?.trim()) return
    editor.commands.setYoutubeVideo({ src: url.trim() })
  }, [editor])

  if (!editor) {
    return (
      <div
        className={`rounded-2xl border border-primary/15 bg-slate-50 animate-pulse ${className}`}
        style={{ minHeight }}
      />
    )
  }

  const words = editor.storage.characterCount?.words?.() ?? 0
  const chars = editor.storage.characterCount?.characters?.() ?? 0

  return (
    <div className={`rounded-2xl border border-primary/15 bg-white overflow-hidden shadow-sm ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => void onPickImage(e.target.files?.[0])}
      />
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-primary/10 bg-slate-50/95 backdrop-blur px-2 py-1.5">
        <ToolBtn title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo2 className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo2 className="h-4 w-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn
          title="Heading 1"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <Heading1 className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Heading 2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Heading 3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="h-4 w-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn
          title="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Underline"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Strike"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Highlight"
          active={editor.isActive('highlight')}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="h-4 w-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn
          title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Quote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Code block"
          active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <Code className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="h-4 w-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          <AlignLeft className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          <AlignCenter className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          <AlignRight className="h-4 w-4" />
        </ToolBtn>
        <Divider />
        <ToolBtn title="Link" active={editor.isActive('link')} onClick={setLink}>
          <Link2 className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title={uploading ? 'Uploading…' : 'Upload image'} onClick={addImage} disabled={uploading}>
          <ImageIcon className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="YouTube" onClick={addYoutube}>
          <YoutubeIcon className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Insert table"
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          <TableIcon className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn
          title="Clear formatting"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <RemoveFormatting className="h-4 w-4" />
        </ToolBtn>
      </div>

      <EditorContent editor={editor} />

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-primary/10 bg-slate-50/80 px-3 py-1.5 text-[0.7rem] text-slate-500">
        <span>
          {words} words · {chars} chars · ~{Math.max(1, Math.round(words / 220))} min read
          {uploading ? ' · uploading image…' : ''}
        </span>
        <span className="text-slate-400">
          Images: 1200–1600px wide · under 2MB · click → resize / align / alt
        </span>
      </div>
    </div>
  )
}

function stripEmpty(html: string) {
  return html.replace(/\s+/g, '').replace(/<p><\/p>/g, '').replace(/<p><br\/?><\/p>/g, '')
}
